import { useNavigate } from 'react-router-dom';

/**
 * DetailPageNavigation — Prev/Next/Close strip at the bottom of a detail
 * page (Phase 5d-1 follow-up — operator-requested standard pattern).
 *
 * Sits below the staff action zone so the natural "decided + move on" flow
 * is one keyboard tab away. Prev/Next walk the queue order the operator
 * arrived from; Close drops back to the parent list. The queue context is
 * passed via React Router location.state from the parent list page — when
 * absent (e.g. direct URL load) Prev/Next are hidden cleanly so the strip
 * still surfaces Close.
 *
 * Queue state shape used across the admin surfaces:
 *
 *   interface QueueContext {
 *     queue: string[];          // ordered list of resource ids
 *     currentIdx: number;       // index of the current id in `queue`
 *     basePath: string;         // e.g. '/admin/partners' — id is appended
 *     backTo: string;           // where Close navigates
 *   }
 */
export interface QueueContext {
  queue: string[];
  currentIdx: number;
  basePath: string;
  backTo: string;
}

export interface DetailPageNavigationProps {
  context: QueueContext | null;
  backTo: string;
}

export function DetailPageNavigation({ context, backTo }: DetailPageNavigationProps) {
  const navigate = useNavigate();

  const hasPrev = context !== null && context.currentIdx > 0;
  const hasNext = context !== null && context.currentIdx < context.queue.length - 1;

  function goRelative(delta: -1 | 1) {
    if (!context) return;
    const targetIdx = context.currentIdx + delta;
    if (targetIdx < 0 || targetIdx >= context.queue.length) return;
    const targetId = context.queue[targetIdx];
    const newContext: QueueContext = {
      ...context,
      currentIdx: targetIdx,
    };
    navigate(`${context.basePath}/${encodeURIComponent(targetId)}`, {
      state: { queueContext: newContext },
    });
  }

  return (
    <nav className="portal-detail-nav" aria-label="Detail page navigation">
      {context !== null && (
        <span className="portal-detail-nav-position">
          {context.currentIdx + 1} of {context.queue.length}
        </span>
      )}
      <div className="portal-detail-nav-actions">
        <button
          type="button"
          className="portal-detail-nav-btn"
          onClick={() => goRelative(-1)}
          disabled={!hasPrev}
          aria-label="Open previous Brief"
        >
          ← Previous
        </button>
        <button
          type="button"
          className="portal-detail-nav-btn"
          onClick={() => goRelative(1)}
          disabled={!hasNext}
          aria-label="Open next Brief"
        >
          Next →
        </button>
        <button
          type="button"
          className="portal-detail-nav-btn portal-detail-nav-btn-close"
          onClick={() => navigate(backTo)}
          aria-label="Close and return to list"
        >
          × Close
        </button>
      </div>
    </nav>
  );
}
